import React, { useState, useEffect, useRef } from 'react';
import PhysicalProgressChartDashboard from '../../physicalProgressChart/PhysicalProgressChartDashboard';
import { Ship, Construction, X, Maximize, Minimize, FileUp, Download, CheckCircle, AlertCircle, Loader2, Sparkles } from 'lucide-react'; // Added new imports for loader icons
import { getRequest, postRequest } from '../../services/apiService'; // Ensure postRequest is imported
import RequisitionStatusSection from './RequisitionStatusSection';
import AddNewFormModal from './AddNewFormModal';
import UpdateTaskPopup from '../../master/taskTransaction/UpdateTaskPopup';
import ReusableForm from '../../common/ReusableForm';

// Mock data for requisitions (extended for demonstration of carousel)
const mockRequisitions = [
  {
    id: 1,
    title: 'Material Requisition - Steel Plates',
    status: 'approved',
    submittedBy: 'John Doe',
    submittedDate: '2025-06-20',
    files: [
      { name: 'steel_plates_spec.pdf', type: 'pdf', size: '2.5 MB', url: '#' },
      { name: 'material_list.xlsx', type: 'excel', size: '1.2 MB', url: '#' }
    ],
    links: [
      { title: 'Supplier Quote', url: 'https://example.com/quote1' },
      { title: 'Technical Specifications', url: 'https://example.com/specs1' }
    ]
  },
  {
    id: 2,
    title: 'Equipment Requisition - Welding Tools',
    status: 'pending',
    submittedBy: 'Jane Smith',
    submittedDate: '2025-06-22',
    files: [
      { name: 'welding_tools.pdf', type: 'pdf', size: '1.8 MB', url: '#' },
      { name: 'equipment_photos.zip', type: 'zip', size: '5.3 MB', url: '#' }
    ],
    links: [
      { title: 'Equipment Manual', url: 'https://example.com/manual1' }
    ]
  },
  {
    id: 3,
    title: 'Safety Equipment Request',
    status: 'rejected',
    submittedBy: 'Mike Johnson',
    submittedDate: '2025-06-18',
    files: [
      { name: 'safety_requirements.docx', type: 'word', size: '0.8 MB', url: '#' }
    ],
    links: []
  },
  {
    id: 4,
    title: 'Maintenance Supplies',
    status: 'in_progress',
    submittedBy: 'Sarah Wilson',
    submittedDate: '2025-06-24',
    files: [
      { name: 'maintenance_list.pdf', type: 'pdf', size: '1.1 MB', url: '#' },
      { name: 'cost_estimate.xlsx', type: 'excel', size: '0.9 MB', url: '#' }
    ],
    links: [
      { title: 'Vendor Catalog', url: 'https://example.com/catalog1' },
      { title: 'Previous Orders', url: 'https://example.com/orders1' }
    ]
  },
  {
    id: 5,
    title: 'Office Supplies Order',
    status: 'approved',
    submittedBy: 'David Lee',
    submittedDate: '2025-06-21',
    files: [{ name: 'office_supplies.pdf', type: 'pdf', size: '0.5 MB', url: '#' }],
    links: []
  },
  {
    id: 6,
    title: 'IT Equipment Purchase',
    status: 'pending',
    submittedBy: 'Emily White',
    submittedDate: '2025-06-23',
    files: [{ name: 'it_equipment_quote.pdf', type: 'pdf', size: '1.5 MB', url: '#' }],
    links: [{ title: 'Vendor Website', url: 'https://example.com/vendor' }]
  },
  {
    id: 7,
    title: 'Vehicle Maintenance Request',
    status: 'in_progress',
    submittedBy: 'Chris Green',
    submittedDate: '2025-06-25',
    files: [],
    links: []
  },
  {
    id: 8,
    title: 'New Hire Onboarding Kit',
    status: 'approved',
    submittedBy: 'Olivia Black',
    submittedDate: '2025-06-19',
    files: [{ name: 'onboarding_checklist.docx', type: 'word', size: '0.3 MB', url: '#' }],
    links: []
  },
];

// Dummy data for files to be downloaded
const mockDownloadableFiles = [
    { projectId: '1', shipId: '12', id: 'doc1', name: 'Engine Specs Q2 2025.pdf' },
    { projectId: '1', shipId: '12', id: 'doc2', name: 'Hull Design Blueprints.dwg' },
    { projectId: '1', shipId: '12', id: 'doc3', name: 'Maintenance Schedule.xlsx' },
    { projectId: '1', shipId: '13', id: 'doc4', name: 'Navigation Charts V1.zip' },
    { projectId: '2', shipId: '14', id: 'doc5', name: 'Propeller Repair Manual.pdf' },
    { projectId: '2', shipId: '14', id: 'doc6', name: 'Safety Protocols.docx' },
];

const CustomSelect = ({ icon: Icon, label, value, onChange, options, className = "" }) => (
  <div className={`relative ${className}`}>
    <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-emerald-600">
      <Icon size={18} />
    </div>
    <select
      value={value}
      onChange={onChange}
      className="bg-white border border-gray-200 rounded-xl pl-10 pr-4 py-3 w-full text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm appearance-none transition-all duration-200 hover:border-emerald-300"
      aria-label={label}
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  </div>
);

// Enhanced Beautiful Loader Component
const BarLoader = ({ message, type = 'upload', currentProgress = null }) => {
  const [internalProgress, setInternalProgress] = useState(0);
  const [dots, setDots] = useState('');
  const [sparklePositions, setSparklePositions] = useState([]);

  // Generate random sparkle positions
  useEffect(() => {
    const generateSparkles = () => {
      const positions = [];
      for (let i = 0; i < 8; i++) {
        positions.push({
          x: Math.random() * 100,
          y: Math.random() * 100,
          delay: Math.random() * 2,
          duration: 1 + Math.random() * 2
        });
      }
      setSparklePositions(positions);
    };
    generateSparkles();
  }, []);

  // Animate progress bar or use currentProgress
  useEffect(() => {
    if (currentProgress !== null) {
      setInternalProgress(currentProgress);
    } else {
      const interval = setInterval(() => {
        setInternalProgress((prev) => {
          if (prev >= 100) return 0;
          return prev + Math.random() * 15;
        });
      }, 300);

      return () => clearInterval(interval);
    }
  }, [currentProgress]);

  // Animate dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => {
        if (prev.length >= 3) return '';
        return prev + '.';
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const getIcon = () => {
    switch (type) {
      case 'upload':
        return <FileUp className="w-8 h-8 text-purple-500" />;
      case 'download':
        return <Download className="w-8 h-8 text-orange-500" />;
      case 'success':
        return <CheckCircle className="w-8 h-8 text-green-500" />;
      case 'error':
        return <AlertCircle className="w-8 h-8 text-red-500" />;
      default:
        return <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />;
    }
  };

  const getGradientColors = () => {
    // Applying the requested gradient color across all types for the progress bar
    return 'from-[#2a52be] to-[#1e3a8a]'; 
  };

  return (
    <div className="relative">
      {/* Sparkles Background */}
      <div className="absolute inset-0 pointer-events-none">
        {sparklePositions.map((sparkle, index) => (
          <div
            key={index}
            className="absolute w-2 h-2 text-yellow-400 opacity-60"
            style={{
              left: `${sparkle.x}%`,
              top: `${sparkle.y}%`,
              animationDelay: `${sparkle.delay}s`,
              animationDuration: `${sparkle.duration}s`
            }}
          >
            <Sparkles className="w-full h-full animate-pulse" />
          </div>
        ))}
      </div>

      {/* Main Loader Container */}
      {/* Increased max-w-md to max-w-lg for a slightly larger loader pop-up */}
      <div className="flex flex-col items-center justify-center p-8 bg-gradient-to-br from-white via-gray-50 to-white rounded-3xl shadow-2xl border border-gray-200/50 backdrop-blur-xl max-w-lg mx-auto text-center relative overflow-hidden">
        
        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-gray-200 to-transparent animate-pulse"></div>
          <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(99,102,241,0.1),transparent)] animate-pulse"></div>
        </div>

        {/* Floating Icon */}
        <div className="relative mb-6 p-4 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full shadow-lg animate-bounce">
          <div className="absolute inset-0 bg-gradient-to-br from-white/60 to-transparent rounded-full"></div>
          <div className="relative z-10 animate-pulse">
            {getIcon()}
          </div>
          
          {/* Ripple Effect */}
          <div className="absolute inset-0 rounded-full animate-ping opacity-20 bg-gradient-to-r from-blue-400 to-purple-400"></div>
        </div>

        {/* Enhanced Progress Bar */}
        <div className="w-full bg-gray-200 rounded-full h-3 mb-6 overflow-hidden shadow-inner relative">
          {/* Background shine effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full animate-[shimmer_2s_infinite] w-full"></div>
          
          {/* Main progress bar */}
          <div 
            className={`h-full rounded-full bg-gradient-to-r ${getGradientColors()} relative overflow-hidden transition-all duration-300 ease-out shadow-lg`}
            style={{ width: `${Math.min(internalProgress, 100)}%` }}
          >
            {/* Animated shine overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-[slide_1.5s_infinite] -translate-x-full"></div>
            
            {/* Pulsing glow */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
          </div>
        </div>

        {/* Message with Typing Effect */}
        <div className="mb-4 min-h-[2rem] flex items-center justify-center">
          <p className="text-xl font-bold text-gray-800 animate-pulse">
            {message}
            {currentProgress !== null && <span className="ml-2">{Math.round(internalProgress)}%</span>}
            <span className="text-blue-500 ml-1 animate-pulse">{dots}</span>
          </p>
        </div>

        {/* Subtitle with Fade Animation */}
        <p className="text-sm text-gray-600 animate-fade-in-up opacity-70">
          Please wait, this may take a moment...
        </p>

        {/* Floating Orbs */}
        <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-br from-blue-400 to-purple-400 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-gradient-to-br from-pink-400 to-orange-400 rounded-full opacity-20 animate-pulse animation-delay-1000"></div>
      </div>
    </div>
  );
};


const OfflineDashboard = () => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showUpdatePopup, setShowUpdatePopup] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  const [uploadFormData, setUploadFormData] = useState({ project: '', ship: '', file: null });
  const [downloadFormData, setDownloadFormData] = useState({ project: '', ship: '' });
  const [downloadedFiles, setDownloadedFiles] = useState([]);

  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]); // All ships, regardless of parent project, for main dashboard dropdown
  const [uploadModalShips, setUploadModalShips] = useState([]); // Ships specifically for the upload modal dropdown
  const [downloadModalShips, setDownloadModalShips] = useState([]); // Ships specifically for the download modal dropdown

  const [selectedParentProject, setSelectedParentProject] = useState('');
  const [selectedShipId, setSelectedShipId] = useState('');
  const [requisitions, setRequisitions] = useState(mockRequisitions);
  const [isAddFormMaximized, setIsAddFormMaximized] = useState(false);

  const [isLoadingUpload, setIsLoadingUpload] = useState(false);
  const [uploadLoaderMessage, setUploadLoaderMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0); // New state for upload progress

  const [isLoadingDownload, setIsLoadingDownload] = useState(false);
  const [downloadLoaderMessage, setDownloadLoaderMessage] = useState('');
  const [downloadProgress, setDownloadProgress] = useState(0); // New state for download progress


  useEffect(() => {
    const fetchProjectsAndInitialShips = async () => {
      try {
        const projectResponse = await getRequest('/master/projects/');
        if (projectResponse?.data) {
          setProjects(projectResponse.data);
          const defaultProject = projectResponse.data.find(p => p.id === 1);
          if (defaultProject) {
            setSelectedParentProject(defaultProject.id.toString());
            // Fetch ships for the default project immediately after setting it
            await fetchShipsForProject(defaultProject.id.toString());
          }
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };
    fetchProjectsAndInitialShips();
  }, []);

  const fetchShipsForProject = async (projectId) => {
    if (!projectId) {
      setShips([]);
      setSelectedShipId('');
      return;
    }
    try {
      const response = await getRequest(`/master/projects/?parent_id=${projectId}`);
      if (response?.data) {
        setShips(response.data);
        const defaultShip = response.data.find(s => s.id === 12);
        if (defaultShip) {
          setSelectedShipId(defaultShip.id.toString());
        } else if (response.data.length > 0) {
          // If default ship not found, select the first available ship
          setSelectedShipId(response.data[0].id.toString());
        } else {
          setSelectedShipId(''); // No ships available
        }
      }
    } catch (error) {
      console.error("Error fetching ships:", error);
      setShips([]);
      setSelectedShipId('');
    }
  };

  // Effect to re-fetch ships when selectedParentProject changes for the main dashboard dropdown
  useEffect(() => {
    fetchShipsForProject(selectedParentProject);
  }, [selectedParentProject]);

  // Function to fetch ships specifically for the upload modal's project dropdown
  const fetchUploadModalShips = async (projectId) => {
    if (!projectId) {
      setUploadModalShips([]);
      return;
    }
    try {
      const response = await getRequest(`/master/projects/?parent_id=${projectId}`);
      if (response?.data) {
        setUploadModalShips(response.data.map(ship => ({ value: ship.id.toString(), label: ship.name })));
      }
    } catch (error) {
      console.error("Error fetching upload modal ships:", error);
      setUploadModalShips([]);
    }
  };

  // Function to fetch ships specifically for the download modal's project dropdown
  const fetchDownloadModalShips = async (projectId) => {
    if (!projectId) {
      setDownloadModalShips([]);
      return;
    }
    try {
      const response = await getRequest(`/master/projects/?parent_id=${projectId}`);
      if (response?.data) {
        setDownloadModalShips(response.data.map(ship => ({ value: ship.id.toString(), label: ship.name })));
      }
    } catch (error) {
      console.error("Error fetching download modal ships:", error);
      setDownloadModalShips([]);
    }
  };


  const handleOpenForm = (mode) => {
    if (mode === 'add') {
      setShowAddForm(true);
    } else if (mode === 'update') {
      setShowUpdatePopup(true);
    }
  };

  const handleCloseAddForm = () => {
    setShowAddForm(false);
    setIsAddFormMaximized(false);
  };

  const handleCloseUpdatePopup = () => {
    setShowUpdatePopup(false);
  };

  // Upload Modal Handlers
  const handleOpenUploadModal = async () => {
    setShowUploadModal(true);
    // Initialize uploadFormData with current main dashboard selections
    setUploadFormData({ project: selectedParentProject, ship: selectedShipId, file: null });
    // Fetch ships specifically for the upload modal's project dropdown on open
    if (selectedParentProject) {
        await fetchUploadModalShips(selectedParentProject);
    }
    setIsLoadingUpload(false);
    setUploadLoaderMessage('');
    setUploadProgress(0); // Reset progress on open
  };

  const handleCloseUploadModal = () => {
    setShowUploadModal(false);
    setUploadFormData({ project: '', ship: '', file: null });
    setUploadModalShips([]); // Clear upload modal specific ships
    setIsLoadingUpload(false);
    setUploadLoaderMessage('');
    setUploadProgress(0); // Reset progress on close
  };

  // Modified handleUploadFile to incorporate PERT logic and progress simulation
  const handleUploadFile = async (e) => {
    e.preventDefault();

    if (!uploadFormData.file) {
      alert('Please select a file to upload.');
      return;
    }

    if (!uploadFormData.ship) {
      alert('Ship is required for upload.');
      return;
    }

    setIsLoadingUpload(true);
    setUploadLoaderMessage('Preparing file for upload...');
    setUploadProgress(10); // Initial progress

    await new Promise(resolve => setTimeout(resolve, 1000)); 
    setUploadLoaderMessage('Compressing and encrypting data...');
    setUploadProgress(30);

    await new Promise(resolve => setTimeout(resolve, 1000));
    setUploadLoaderMessage('Uploading file to server...');
    setUploadProgress(60);

    // Simulate more progress during the actual upload attempt
    await new Promise(resolve => setTimeout(resolve, 1500)); // Simulating network transfer time
    setUploadProgress(90);

    try {
      const formData = new FormData();
      formData.append('file', uploadFormData.file, uploadFormData.file.name);
      formData.append('project_id', uploadFormData.ship); 

      // Use postRequest from apiService, with true for multipart/form-data
      const pertResponse = await postRequest(`pert/upload-pert/`, formData, true); 

      setUploadProgress(100); // Complete progress
      setUploadLoaderMessage('File uploaded successfully!');
      await new Promise(resolve => setTimeout(resolve, 1500)); // Keep loader visible briefly for completion message

      alert(`File "${uploadFormData.file.name}" uploaded successfully for Ship ID: ${uploadFormData.ship}`);

    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.detail || 'File upload failed.';
      setUploadLoaderMessage(`Upload failed: ${errorMessage}`); // Set failed message
      setUploadProgress(0); // Reset progress on error
      await new Promise(resolve => setTimeout(resolve, 2500)); // Keep loader visible for error message
      alert(`Upload failed: ${errorMessage}`);

    } finally {
      setIsLoadingUpload(false);
      setUploadLoaderMessage('');
      setUploadProgress(0); // Ensure progress is reset
      handleCloseUploadModal(); // Close modal whether success or failure
    }
  };

  // Download Modal Handlers
  const handleOpenDownloadModal = async () => {
    setShowDownloadModal(true);
    setDownloadFormData({ project: selectedParentProject, ship: selectedShipId });
    // Fetch ships specifically for the download modal's project dropdown on open
    if (selectedParentProject) {
      await fetchDownloadModalShips(selectedParentProject);
    }
    setDownloadedFiles([]);
    setIsLoadingDownload(false);
    setDownloadLoaderMessage('');
    setDownloadProgress(0); // Reset progress on open
  };

  const handleCloseDownloadModal = () => {
    setShowDownloadModal(false);
    setDownloadFormData({ project: '', ship: '' });
    setDownloadModalShips([]); // Clear download modal specific ships
    setDownloadedFiles([]);
    setIsLoadingDownload(false);
    setDownloadLoaderMessage('');
    setDownloadProgress(0); // Reset progress on close
  };

  const handleDownloadFile = async (e) => {
    e.preventDefault();
    setIsLoadingDownload(true);
    setDownloadLoaderMessage('Searching for files...');
    setDownloadProgress(10); // Initial progress

    await new Promise(resolve => setTimeout(resolve, 1000));
    setDownloadLoaderMessage('Retrieving file metadata...');
    setDownloadProgress(40);

    await new Promise(resolve => setTimeout(resolve, 1000));
    setDownloadLoaderMessage('Compiling available files...');
    setDownloadProgress(70);

    console.log("Fetching files for:", downloadFormData);
    const files = mockDownloadableFiles.filter(file =>
        file.projectId === downloadFormData.project && file.shipId === downloadFormData.ship
    );
    setDownloadedFiles(files);

    setDownloadProgress(100); // Complete progress before message
    setDownloadLoaderMessage('Files data extracted successfully!');
    await new Promise(resolve => setTimeout(resolve, 1500)); // Display message for 1.5 seconds

    setIsLoadingDownload(false); // Then hide the loader
    setDownloadLoaderMessage(''); // Clear message
    setDownloadProgress(0); // Reset progress

    if (files.length === 0) {
      alert(`No files found for Project ID: ${downloadFormData.project}, Ship ID: ${downloadFormData.ship}`);
    }
  };

  const handleIndividualFileDownload = async (fileId, fileName) => {
      setIsLoadingDownload(true);
      setDownloadLoaderMessage(`Preparing "${fileName}" for download...`);
      setDownloadProgress(20);

      await new Promise(resolve => setTimeout(resolve, 1000));
      setDownloadLoaderMessage(`Initiating secure download for "${fileName}"...`);
      setDownloadProgress(60);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setDownloadLoaderMessage(`Download of "${fileName}" complete!`);
      setDownloadProgress(100);
      await new Promise(resolve => setTimeout(resolve, 1500)); // Give a moment for final message

      console.log(`Downloading file: ${fileName} (ID: ${fileId})`);
      alert(`Download of "${fileName}" is complete.`);

      setIsLoadingDownload(false);
      setDownloadLoaderMessage('');
      setDownloadProgress(0);
  };

  // Fields for Upload Form
  const uploadFormFields = [
    {
      name: 'project',
      label: 'Project',
      type: 'select',
      options: projects.map(project => ({ value: project.id.toString(), label: project.name })),
      required: true,
      placeholder: 'Select Project',
      onChange: async (e) => { // Made async to await fetchUploadModalShips
        const projectId = e.target.value;
        setUploadFormData(prev => ({
          ...prev,
          project: projectId,
          ship: '' // Reset ship when project changes
        }));
        await fetchUploadModalShips(projectId); // Fetch ships for the newly selected project in the modal
      }
    },
    {
      name: 'ship',
      label: 'Ship',
      type: 'select',
      options: uploadModalShips, // Use the new uploadModalShips state
      required: true,
      placeholder: 'Select Ship',
      disabled: (formData) => !formData.project || uploadModalShips.length === 0, // Disable if no project or no ships
    },
    {
      name: 'file',
      label: 'Select File',
      type: 'file',
      required: true,
      // Handle file change to update uploadFormData.file
      onChange: (e) => setUploadFormData(prev => ({ ...prev, file: e.target.files[0] }))
    },
  ];

  // Fields for Download Form
  const downloadFormFields = [
    {
      name: 'project',
      label: 'Project',
      type: 'select',
      options: projects.map(project => ({ value: project.id.toString(), label: project.name })),
      required: true,
      placeholder: 'Select Project',
      onChange: async (e) => { // Added async onChange to update downloadModalShips
        const projectId = e.target.value;
        setDownloadFormData(prev => ({
          ...prev,
          project: projectId,
          ship: '' // Reset ship when project changes
        }));
        await fetchDownloadModalShips(projectId); // Fetch ships for the newly selected project in the download modal
      }
    },
    {
      name: 'ship',
      label: 'Ship',
      type: 'select',
      options: downloadModalShips, // Use the new downloadModalShips state
      required: true,
      placeholder: 'Select Ship',
      disabled: (formData) => !formData.project || downloadModalShips.length === 0, // Disable if no project or no ships
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50">
      {/* Add a style block for the new animations */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes slide {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        
        @keyframes fade-in-up {
          0% { 
            opacity: 0; 
            transform: translateY(10px); 
          }
          100% { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out;
        }
        
        .animation-delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
      <div className="max-w-full mx-auto p-4 sm:p-6 lg:p-8">
        <header className="sticky top-4 z-30 w-full mb-8 bg-white/80 backdrop-blur-xl border border-white/20 rounded-3xl shadow-xl px-6 py-6 flex flex-col lg:flex-row items-start lg:items-center gap-6">
          {/* Title Section */}
          <div className="flex-shrink-0">
            <h1 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Offline Dashboard
            </h1>
            <p className="text-gray-600 mt-1">Manage your projects and requisitions</p>
          </div>

          {/* Dropdowns Section */}
          <div className="flex flex-col sm:flex-row flex-1 gap-4 w-full lg:w-auto">
            <CustomSelect
              icon={Construction}
              label="Project"
              value={selectedParentProject}
              onChange={(e) => setSelectedParentProject(e.target.value)}
              options={projects.map(project => ({ value: project.id, label: project.name }))}
              className="w-full sm:w-64"
            />
            <CustomSelect
              icon={Ship}
              label="Ship"
              value={selectedShipId}
              onChange={(e) => setSelectedShipId(e.target.value)}
              options={ships.map(ship => ({ value: ship.id, label: ship.name }))}
              className="w-full sm:w-64"
            />
          </div>

          {/* Buttons Section */}
          <div className="flex flex-wrap gap-3 w-full lg:w-auto">
            <button
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              onClick={() => handleOpenForm('update')}
            >
              Update
            </button>
            <button
              className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl font-semibold hover:from-emerald-700 hover:to-emerald-800 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              onClick={() => handleOpenForm('add')}
            >
              Add New
            </button>
            {/* New Upload Button */}
            <button
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-xl font-semibold hover:from-purple-700 hover:to-purple-800 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              onClick={handleOpenUploadModal}
            >
              Upload File
            </button>
            {/* New Download Button */}
            <button
              className="px-6 py-3 bg-gradient-to-r from-orange-600 to-orange-700 text-white rounded-xl font-semibold hover:from-orange-700 hover:to-orange-800 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
              onClick={handleOpenDownloadModal}
            >
              Download File
            </button>
          </div>
        </header>

        {/* Requisition Status Section */}
        <RequisitionStatusSection requisitions={requisitions} />

        {/* Physical Progress Chart */}
        {/* <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <PhysicalProgressChartDashboard selectedShipId={selectedShipId} />
        </div> */}
      </div>

      {/* Update Task Popup */}
      {showUpdatePopup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
              <h3 className="text-xl font-bold">Update Task</h3>
              <button onClick={handleCloseUpdatePopup} className="text-white hover:text-gray-200 transition-colors">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <UpdateTaskPopup isOpen={showUpdatePopup} onClose={handleCloseUpdatePopup} />
            </div>
          </div>
        </div>
      )}

      {/* Add Form Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className={`bg-white rounded-2xl shadow-2xl ${isAddFormMaximized ? 'w-full h-full max-w-full max-h-full' : 'w-full max-w-6xl max-h-[90vh]'} overflow-hidden transition-all duration-300 ease-in-out`}>
            <div className="flex items-center justify-between px-6 py-4" style={{ backgroundColor: 'rgb(30, 58, 138)' }}> {/* Applied new color */}
              <h3 className="text-xl font-bold text-white">Add New Form</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsAddFormMaximized(!isAddFormMaximized)}
                  className="text-white hover:text-gray-200 transition-colors"
                  aria-label={isAddFormMaximized ? "Minimize" : "Maximize"}
                >
                  {isAddFormMaximized ? <Minimize size={24} /> : <Maximize size={24} />}
                </button>
                <button onClick={handleCloseAddForm} className="text-white hover:text-gray-200 transition-colors" aria-label="Close">
                  <X size={24} />
                </button>
              </div>
            </div>
            <div className={`p-6 ${isAddFormMaximized ? 'h-[calc(100vh-80px)]' : 'max-h-[calc(90vh-120px)]'} overflow-y-auto`}>
              <AddNewFormModal onClose={handleCloseAddForm} />
            </div>
          </div>
        </div>
      )}

      {/* Upload File Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden relative"> {/* Added relative for loader positioning */}
            <ReusableForm
              formData={uploadFormData}
              setFormData={setUploadFormData}
              fields={uploadFormFields}
              onSubmit={handleUploadFile}
              onHide={handleCloseUploadModal}
              headerText="Upload File"
              submitLabel="Upload"
            />
            {isLoadingUpload && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-10">
                    <BarLoader message={uploadLoaderMessage} type="upload" currentProgress={uploadProgress} />
                </div>
            )}
          </div>
        </div>
      )}

      {/* Download File Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden relative"> {/* Added relative for loader positioning */}
            <ReusableForm
              formData={downloadFormData}
              setFormData={setDownloadFormData}
              fields={downloadFormFields}
              onSubmit={handleDownloadFile}
              onHide={handleCloseDownloadModal}
              headerText="Download Files"
              submitLabel="Show Files"
            />

            {downloadedFiles.length > 0 && (
              <div className="p-6 border-t border-gray-200 mt-4 max-h-[300px] overflow-y-auto">
                <h4 className="text-lg font-semibold mb-3 text-gray-800">Available Files:</h4>
                <ul className="space-y-2">
                  {downloadedFiles.map((file) => (
                    <li key={file.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-md border border-gray-200">
                      <span className="text-gray-700 font-medium">{file.name}</span>
                      <button
                        onClick={() => handleIndividualFileDownload(file.id, file.name)}
                        className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors"
                      >
                        Download
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {downloadedFiles.length === 0 && downloadFormData.project && downloadFormData.ship && (
                <div className="p-6 text-center text-gray-500">
                    No files found for the selected Project and Ship.
                </div>
            )}

            {isLoadingDownload && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 backdrop-blur-sm z-10">
                    <BarLoader message={downloadLoaderMessage} type="download" currentProgress={downloadProgress} />
                </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OfflineDashboard;